<template>
  <Modal :show="true" @close="$emit('close')" clickaway class="project-shops-update">
    <Loader :value="promises.fetch" theme="overlay">
      <Card tag="form" theme="flat" @submit.prevent="submit">
        <template #default>
          <div class="project-shops-update__expiry">
            <InputComponent :label="labels['signableFrom']" data-field="signableFrom">
              <template #default>
                <InputDate
                  :value="format(data.signableFrom)"
                  :disabled="!querystring"
                  @input="$ev => setDate('data.signableFrom', $ev)"
                />
              </template>
            </InputComponent>
            <InputComponent :label="labels['expiresAt']" data-field="expiresAt">
              <template #default>
                <InputDate
                  :value="format(data.expiresAt)"
                  :disabled="!querystring"
                  @input="$ev => setDate('data.expiresAt', $ev)"
                />
              </template>
            </InputComponent>
          </div>
          <a
            href="#"
            @click.prevent="() => querystring && (data.partials ? unsetPartials() : setPartials())"
            class="project-shops-update__extend"
          >
            <FontAwesomeIcon :icon="['fal', data.partials ? 'calendar-times' : 'calendar-plus']" />
            {{ $t(data.partials ? 'removePartialDeadlines' : 'setPartialDeadlines') }}
          </a>

          <DataTable
            v-if="data.partials"
            class="project-shops-update__partials"
            theme="border"
            :value="data.partials"
            :columns="[
              { id: 'label', label: $tc('partialDeadline') },
              { id: 'from', label: labels.signableFrom },
              { id: 'expires', label: labels.expiresAt },
            ]"
            :cell-wrapper="
              (item, col) => ({
                component: 'div',
                props: {
                  class: { 'project-shops-update__partial-category': col.id === 'label' },
                },
              })
            "
            :row-wrapper="
              item => ({
                component: 'div',
                props: { class: 'project-shops-update__partial' },
              })
            "
          >
            <template #label="{ item }">
              {{ item.reference }}
            </template>
            <template #from="{ item, rowIndex }">
              <InputComponent data-field="signableFrom">
                <template #default>
                  <InputDate
                    :value="format(item.signableFrom)"
                    :disabled="!querystring"
                    @input="$ev => setDate(`data.partials.${rowIndex}.signableFrom`, $ev)"
                  />
                </template>
              </InputComponent>
            </template>
            <template #expires="{ item, rowIndex }">
              <InputComponent data-field="expiresAt">
                <template #default>
                  <InputDate
                    :value="format(item.expiresAt)"
                    :disabled="!querystring"
                    @input="$ev => setDate(`data.partials.${rowIndex}.expiresAt`, $ev)"
                  />
                </template>
              </InputComponent>
            </template>
          </DataTable>

          <Alert v-if="!querystring" class="mt-2">{{ $t('shopEditDueAtUnitNotSold') }}</Alert>
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="$emit('close')" />
          <ButtonComponent
            :label="$t('confirm')"
            theme="primary"
            type="submit"
            :promise="promises.submit"
            :disabled="!querystring"
            :icon="['fal', 'angle-right']"
            @onSuccess="$emit('close')"
          />
        </template>
      </Card>
    </Loader>
  </Modal>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import { mapi18n } from '@/i18n'
import moment from 'moment'
import { setByPath } from '@/utils'
import fetch from '@/fetch'

import { ModalComponent as Modal } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { ButtonComponent } from 'vue-elder-button'
import Card from '@/components/Card'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import InputDate from '@/components/InputDate'
import { AlertComponent as Alert } from 'vue-elder-alert'
import DataTable from '@/components/DataTable'

export default {
  props: {
    residentials: Array,
  },
  mixins: [
    Validator({
      labels: 'labels',
      rules: {},
      data: 'data',
    }),
  ],
  watch: {
    residentials: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      item: null,
      data: {
        signableFrom: null,
        expiresAt: null,
        partials: null,
      },
      promises: {
        fetch: null,
        submit: null,
      },
    }
  },
  computed: {
    ...mapi18n('labels', {
      expiresAt: 'deadline',
      signableFrom: 'signableFrom',
    }),
    querystring() {
      let qs = new URLSearchParams()
      this.residentials.filter(r => r.shop.contract).forEach(r => qs.append('ids', r.shop.contract.id))
      return qs.toString()
    },
  },
  methods: {
    format(val) {
      if (!val) return
      return moment(val).format('YYYY-MM-DD')
    },
    setDate(path, value) {
      if (value) {
        switch (path.split('.').pop()) {
          case 'signableFrom':
            value = moment(value).startOf('day').toDate()
            break
          case 'expiresAt':
            value = moment(value).endOf('day').toDate()
            break
          default:
            value = moment(value).toDate()
            break
        }
      }
      setByPath(path, value || null, this)
    },
    setPartials() {
      this.data.partials = this.item.partials
    },
    unsetPartials() {
      this.data.partials = null
    },
    submit() {
      this.promises.submit = fetch(`/api/contract/shop/dates?${this.querystring}`, {
        method: 'POST',
        body: JSON.stringify(this.data),
      })
    },
    fetch() {
      if (!this.querystring) return
      this.promises.fetch = fetch(`/api/contract/shop/dates?${this.querystring}`)
        .then(res => res.json())
        .then(data => {
          this.item = data

          this.data.signableFrom = data.signableFrom
          this.data.expiresAt = data.expiresAt

          if (this.item.partials.some(p => p.signableFrom || p.expiresAt)) this.setPartials()
        })
    },
  },
  components: {
    Modal,
    InputComponent,
    Card,
    InputDate,
    ButtonComponent,
    Loader,
    Alert,
    DataTable,
  },
}
</script>

<style lang="scss">
.project-shops-update {
  &__expiry {
    display: flex;
    gap: 1rem;
  }

  .card,
  .elder-loader {
    max-width: 600px;
    margin: 0 auto;
  }

  .elder-loader__element {
    border-radius: var(--border-radius-md);
  }

  &__partial-category {
    padding: 1em !important;
  }

  .data-table {
    margin-top: 1rem;
    width: 100%;

    &__cell {
      padding: 0;
      font-size: var(--font-size-small);
      border-color: var(--border-color) !important;
      display: block;
    }

    &__row {
      &:not(:first-child) {
        .data-table__cell {
          border-right: 1px solid;

          &:first-child {
            border-left: 1px solid;
          }
        }
      }

      &:last-child .data-table__cell {
        border-bottom: 1px solid;
      }

      .data-table__cell:first-child {
        padding: 0.5rem !important;
      }
    }

    .elder-input__field {
      border: none;
      background-color: transparent;
    }
  }

  &__extend {
    text-decoration: none;
    display: block;
    font-size: 0.8em;
    margin-top: 0.5rem;

    svg {
      margin-right: 5px;
    }
  }
}
</style>
